import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalBody,
  ModalHeader,
  Button,
  Input,
  Label,
  Row,
  Col,
} from "reactstrap";
import Select from "react-select";
import { fixedWidthCustomStyle } from "constants/CommonConstants";
import { LANGUAGE_OPTIONS } from "components/constant";

import { useSelector } from "react-redux";

const initialFields = {
  name: { value: "", error: "" },
  language: { value: "", error: "" },
  tagID: { value: "", error: "" },
  // gradingType: { value: "", error: "" },
};

const LanguageModal = ({ isOpen, toggleModal, onSave, loaderOnClick }) => {
  const [fields, setFields] = useState(initialFields);
  const tagsData = useSelector((state) => state.tags.tagsList); //remove the getTagList() api call as parent QuestionBankList call that api
  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFields(initialFields);
    }
  }, [isOpen]);

  const tagOptions =
    tagsData?.data?.Tag?.filter((tag) => tag.status === "ACTIVE").map(
      (tag) => ({
        value: tag.ID,
        label: tag.name,
      }),
    ) || [];

  const handleChange = (name, value) => {
    setFields((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value,
        error: "",
      },
    }));
  };

  const getValidationError = (name, value) => {
    const trimmed = typeof value === "string" ? value.trim() : value;

    switch (name) {
      case "name":
        return !trimmed ? "Please enter question bank name" : "";

      case "language":
        return !trimmed ? "Please select language" : "";

      case "tagID":
        return !trimmed ? "Please select tag" : "";

      // case "gradingType":
      //   return !trimmed ? "Please select grading type" : "";

      default:
        return "";
    }
  };

  const handleOnBlur = (name, value) => {
    setFields((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: typeof value === "string" ? value.trim() : value,
        error: getValidationError(name, value),
      },
    }));
  };

  const validateForm = () => {
    let updated = { ...fields };

    Object.keys(updated).forEach((key) => {
      updated[key] = {
        ...updated[key],
        error: getValidationError(key, updated[key].value),
      };
    });

    setFields(updated);

    return Object.values(updated).every((item) => item.error === "");
  };

  const handleSave = () => {
    if (!validateForm()) return;
    // Extract only field values from form state
    // Removes error objects and keeps API-required data
    const payload = Object.keys(fields).reduce((acc, key) => {
      acc[key] = fields[key].value;
      return acc;
    }, {});

    onSave({
      ...payload,
      status: "ACTIVE",
      // gradingType: fields.gradingType.value,
    });
  };

  return (
    <>
      {isOpen && <div className="blur-overlay"></div>}

      <Modal
        isOpen={isOpen}
        toggle={toggleModal}
        backdrop={false}
        keyboard={false}
        className="modal-dialog-centered"
      >
        <ModalHeader toggle={toggleModal}>
          <span className="h4 mb-0 text-default section-title">
            Please fill question bank details
          </span>
        </ModalHeader>

        <ModalBody>
          {/* QUESTION BANK NAME */}
          <div className="mb-3">
            <Label className="small-label text-muted">
              Question Bank Name <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              placeholder="Enter question bank name"
              value={fields.name.value}
              onChange={(e) => handleChange("name", e.target.value)}
              onBlur={(e) => handleOnBlur("name", e.target.value)}
              maxLength={100}
              className={fields.name.error ? "is-invalid" : ""}
            />
            {fields.name.error && (
              <div className="invalid-feedback d-block">
                {fields.name.error}
              </div>
            )}
          </div>

          {/* LANGUAGE */}
          <div className="mb-3">
            <Label className="small-label text-muted">
              Language <span className="text-danger">*</span>
            </Label>
            <div
              className={`language-options ${
                fields.language.error ? "has-danger" : ""
              }`}
            >
              {LANGUAGE_OPTIONS.map((item) => (
                <div
                  key={item.value}
                  className={`language-card ${
                    fields.language.value === item.value ? "active" : ""
                  }`}
                  onClick={() => handleChange("language", item.value)}
                >
                  <Input
                    type="radio"
                    name="language"
                    checked={fields.language.value === item.value}
                    onChange={() => handleChange("language", item.value)}
                    className={fields.language.error ? "is-invalid" : ""}
                  />
                  <span className="small-label-text ml-2">{item.label}</span>
                </div>
              ))}
            </div>
            {fields.language.error && (
              <div className="invalid-feedback d-block">
                {fields.language.error}
              </div>
            )}
          </div>

          <Row className="mb-3">
            <Col md="6">
              <Label className="small-label text-muted">
                Tag <span className="text-danger">*</span>
              </Label>
              <div className={fields.tagID.error ? "has-danger" : ""}>
                <Select
                  classNamePrefix="react-select"
                  styles={fixedWidthCustomStyle("100%")}
                  options={tagOptions}
                  placeholder="Select tag"
                  value={tagOptions.find(
                    (item) => item.value === fields.tagID.value,
                  )}
                  onChange={(selected) =>
                    handleChange("tagID", selected?.value || "")
                  }
                  onBlur={() => handleOnBlur("tagID", fields.tagID.value)}
                />
              </div>

              {fields.tagID.error && (
                <div className="invalid-feedback d-block">
                  {fields.tagID.error}
                </div>
              )}
            </Col>
            {/* <Col md="6">
              <Label>
                Negative Grading <span className="text-danger">*</span>
              </Label>

              <div className="d-flex mt-2">
                <div className="mr-5 ml-4">
                  <Label check>
                    <Input
                      type="radio"
                      name="gradingType"
                      checked={fields.gradingType.value === "NEGATIVE"}
                      onChange={() => handleChange("gradingType", "NEGATIVE")}
                    />
                    Yes
                  </Label>
                </div>

                <div>
                  <Label check>
                    <Input
                      type="radio"
                      name="gradingType"
                      checked={fields.gradingType.value === "POSITIVE"}
                      onChange={() => handleChange("gradingType", "POSITIVE")}
                    />
                    No
                  </Label>
                </div>
              </div>
            </Col> */}
          </Row>

          {/* ACTION BUTTONS */}
          <div className="d-flex justify-content-end mt-4">
            <Button
              color="secondary"
              onClick={toggleModal}
              className="mr-2 px-4"
            >
              Cancel
            </Button>

            <Button color="deep-blue" onClick={handleSave} className="px-4">
              {loaderOnClick ? (
                <i className="fa fa-spinner fa-spin fa-lg text-white" />
              ) : (
                "Save and Proceed"
              )}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default LanguageModal;
